import React from 'react'
import {
  CircularProgress,
  Box,
  TextField,
  MenuItem,
  colors,
  FormControlLabel,
  Switch,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material'

import useMotivationalQuotes from '../hooks/api/useMotivationalQuotes'
import ListEditor from '../common/ListEditor'
import SettingsFooter from '../settings/shared/SettingsFooter'
import useAlert from '../hooks/useAlert'
import { MOTIVATIONAL_QUOTE_COLOR_OPTIONS } from '../constants'

const MAX_ALLOWED_MOTIVATIONAL_QUOTES = 10

const MotivationalQuotes = () => {
  const {
    motivationalQuotes,
    setMotivationalQuotes,
    saveMotivationalQuotes,
    reset,
    isLoading,
    hasLoadError,
    displayLocation,
    setDisplayLocation,
  } = useMotivationalQuotes()

  const { createAlert, createGenericErrorAlert } = useAlert()

  if (isLoading) return <CircularProgress />

  const onSuccess = () =>
    createAlert({
      severity: 'success',
      message: 'Successfully saved motivational quotes',
    })

  const onFailure = () => createGenericErrorAlert()

  const onSave = () => {
    saveMotivationalQuotes().then(onSuccess).catch(onFailure)
  }

  const isValid = motivationalQuotes.every((mq) => !!mq.quoteText)

  const clearQuotes = (quote) => {
    setMotivationalQuotes((prev) =>
      prev.map((q) => ({ ...q, isSelected: quote === q ? true : false }))
    )
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '750px', maxWidth: '80%', mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControl>
            <FormLabel>Motivational Quote Display Locations</FormLabel>
            <RadioGroup
              name='motivational-quote-display-locations'
              value={displayLocation}
              onChange={(e) => setDisplayLocation(e.target.value)}
            >
              <FormControlLabel
                value='dashboard-only'
                control={<Radio />}
                label='Dashboard Only'
              />
              <FormControlLabel
                value='all-screens'
                control={<Radio />}
                label='All Screens'
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box>
          <FormLabel>Motivational Quotes</FormLabel>
        </Box>
        <FormControlLabel
          sx={{ mb: 2 }}
          label={'Show me a random quote'}
          control={
            <Switch
              checked={!motivationalQuotes.some((q) => q.isSelected)}
              onChange={(e) => {
                clearQuotes(e.target.checked ? null : motivationalQuotes[0])
              }}
            />
          }
        ></FormControlLabel>

        <ListEditor
          list={motivationalQuotes}
          editableKeys={[
            {
              label: 'Selected',
              type: 'boolean',
              key: 'isSelected',
              onChange: (item, val) => {
                if (val) return clearQuotes(item)

                setMotivationalQuotes((prev) =>
                  prev.map((q) => ({
                    ...q,
                    isSelected: q === item ? false : q.isSelected,
                  }))
                )
              },
            },
            {
              label: 'Text',
              key: 'quoteText',
            },
            {
              label: 'Font Color',
              key: 'color',
              isDropdown: true,
              options: MOTIVATIONAL_QUOTE_COLOR_OPTIONS.map((c) => ({
                name: c.charAt(0).toUpperCase() + c.slice(1),
                id: c,
                props: {
                  sx: { color: colors[c]?.[700] },
                },
              })),
            },
          ]}
          onChange={setMotivationalQuotes}
          maxLength={MAX_ALLOWED_MOTIVATIONAL_QUOTES}
        />

        <SettingsFooter
          cancelButton={{
            onClick: reset,
          }}
          saveButton={{ onClick: onSave, disabled: !isValid }}
        ></SettingsFooter>
      </Box>
    </Box>
  )
}

export default MotivationalQuotes
